import { ChevronDown } from "lucide-react";

import { useAuth } from "../auth/AuthContext";
import { CHAPTER } from "../chapter";
import { trpc } from "../trpc";

import { ApplyForm } from "./ApplyForm";

/**
 * Landing page for anyone signed in without access: no role, or a
 * member role in a chapter that runs no memberships.
 *
 *   no application yet      → who switches access on, and a re-check
 *   application = pending   → "we've got it" copy
 *   application = active    → shouldn't happen here (role would be
 *                             set) but render a graceful state
 *   application = cancelled → withdrawn copy
 */

const primaryButtonClass =
  "inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full bg-[color:var(--color-ink)] px-6 text-[15px] font-semibold text-[color:var(--color-paper)] transition-opacity hover:opacity-85";
const secondaryButtonClass =
  "inline-flex min-h-11 cursor-pointer items-center justify-center rounded-full border border-[color:var(--color-line-strong)] bg-[color:var(--color-paper)] px-5 text-[15px] font-medium text-[color:var(--color-ink)] transition-colors hover:border-[color:var(--color-ink)]";

/** ", Priya" after a greeting, or nothing when the account has no name. */
function nameSuffix(firstName: string): string {
  return firstName ? `, ${firstName}` : "";
}

export function AwaitingApproval() {
  const auth = useAuth();
  const { user, signOut } = auth;
  // Memberships only exist where the chapter runs them. Anywhere else a
  // stray application record means nothing, so it reads as no access.
  const application = CHAPTER.features.includes("members")
    ? auth.application
    : null;
  const firstName = user?.fullName?.trim().split(/\s+/)[0] ?? "";
  const utils = trpc.useUtils();

  // After a successful submit, refetch adminMe so the page swaps from
  // "show the form" to "we've got your application" without a reload.
  const refetchMe = () => {
    void utils.adminMe.invalidate();
  };

  const teamAppLine = (
    <p className="text-[15px] text-[color:var(--color-ink-soft)]">
      {CHAPTER.name} team app
    </p>
  );

  const signOutButton = (
    <button
      type="button"
      onClick={() => {
        void signOut();
      }}
      className={secondaryButtonClass}
    >
      Sign out
    </button>
  );

  if (!application) {
    // A volunteer who signs up before someone sets their role used to
    // land on a paid membership application — a different thing
    // entirely, that they'd fill in and pay for by mistake. Team access
    // is granted, not applied for, so say that plainly and offer the
    // membership form only where the chapter runs memberships.
    return (
      <main className="min-h-screen bg-[color:var(--color-bg)] px-5 py-10 md:py-20">
        <div className="mx-auto max-w-2xl">
          {teamAppLine}
          <h1 className="page-title mt-3">
            You&apos;re signed in{nameSuffix(firstName)}
          </h1>
          <p className="mt-2 text-[20px] leading-snug text-[color:var(--color-amber)]">
            You don&apos;t have access to any pages yet.
          </p>
          <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
            An organiser needs to switch on your access. Tell them the email
            address you signed in with
            {user?.email ? (
              <>
                {" "}
                (
                <strong className="font-semibold break-all text-[color:var(--color-ink)]">
                  {user.email}
                </strong>
                )
              </>
            ) : null}
            . Then tap Check again.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className={primaryButtonClass}
            >
              Check again
            </button>
            {signOutButton}
          </div>

          {CHAPTER.features.includes("members") ? (
            <details className="group mt-12 border-t border-[color:var(--color-line)] pt-4">
              <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 text-[15px] font-medium text-[color:var(--color-ink)] [&::-webkit-details-marker]:hidden">
                I want to become a member of the association
                <ChevronDown
                  size={18}
                  aria-hidden="true"
                  className="shrink-0 text-[color:var(--color-ink-soft)] transition-transform group-open:rotate-180"
                />
              </summary>
              <p className="mt-3 max-w-xl text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
                That&apos;s separate from team access. Only fill this in if you
                want to join as a member.
              </p>
              <div className="mt-6">
                <ApplyForm onSubmitted={refetchMe} />
              </div>
            </details>
          ) : null}
        </div>
      </main>
    );
  }

  const isPending = application.status === "pending";
  const isCancelled = application.status === "cancelled";
  const isActive = application.status === "active";
  const inbox = (
    <a
      href={`mailto:${CHAPTER.contactInbox}`}
      className="break-all text-[color:var(--color-amber)] underline underline-offset-4"
    >
      {CHAPTER.contactInbox}
    </a>
  );

  return (
    <main className="flex min-h-screen items-center justify-center bg-[color:var(--color-bg)] px-5 py-10 md:py-20">
      <div className="mx-auto max-w-xl text-center">
        {teamAppLine}
        <h1 className="page-title mt-3">
          {isPending
            ? `Thanks${nameSuffix(firstName)}. We've got your application.`
            : isCancelled
              ? `Hi${nameSuffix(firstName)}. Your application was withdrawn.`
              : isActive
                ? `You're approved${nameSuffix(firstName)}.`
                : `Hi${nameSuffix(firstName)}.`}
        </h1>

        <p className="mt-5 text-[16px] leading-relaxed text-[color:var(--color-ink-soft)]">
          {isPending ? (
            "An organiser will look at it and email you. There's nothing else you need to do here."
          ) : isCancelled ? (
            <>
              If this was a mistake, email {inbox} and we&apos;ll open it again.
            </>
          ) : isActive ? (
            "Sign out and sign back in to see your membership."
          ) : (
            <>
              Something didn&apos;t load properly. Refresh the page, and if you
              still see this, email {inbox}.
            </>
          )}
        </p>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <a
            href={`mailto:${CHAPTER.contactInbox}`}
            className={secondaryButtonClass}
          >
            Email us
          </a>
          {signOutButton}
        </div>
      </div>
    </main>
  );
}
